import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Table, TableBody, TableCell,
  TableHead, TableHeader, TableRow
} from '@/components/ui/table';
import {
  Card, CardContent,
  CardHeader, CardTitle, CardDescription
} from '@/components/ui/card';
import { toast } from '@/components/ui/use-toast';
import { File, Trash2, Search, Eye, Download, X, Loader2 } from 'lucide-react';
import {
  Select, SelectContent,
  SelectItem, SelectTrigger, SelectValue
} from '@/components/ui/select';
import FileUpload from '@/components/ui/FileUpload';
import { companyFilesAPI } from '@/services/companyFiles';

const API_BASE = 'https://new.sbdemolition.online/api/company-files';

const CompanyFilesPage = () => {
  const [files, setFiles]               = useState([]);
  const [loading, setLoading]           = useState(true);
  const [searchTerm, setSearchTerm]     = useState('');
  const [categoryFilter, setCategoryFilter] = useState('All');

  const [uploadCategory, setUploadCategory] = useState('General');
  const [fileDescription, setFileDescription] = useState('');
  const [uploading, setUploading]       = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  // Viewer state
  const [viewerOpen, setViewerOpen]     = useState(false);
  const [viewerUrl, setViewerUrl]       = useState('');
  const [viewerName, setViewerName]     = useState('');
  const [viewerType, setViewerType]     = useState('');
  const [viewerLoading, setViewerLoading] = useState(false);
  const [viewerError, setViewerError]   = useState('');

  useEffect(() => {
    loadFiles();
  }, []);

  // Clean up blob URL when viewer closes
  useEffect(() => {
    if (!viewerOpen && viewerUrl.startsWith('blob:')) {
      URL.revokeObjectURL(viewerUrl);
      setViewerUrl('');
    }
  }, [viewerOpen]);

  const loadFiles = async () => {
    try {
      setLoading(true);
      const response = await companyFilesAPI.getFiles();
      if (response.success) setFiles(response.data || []);
    } catch {
      toast({ title: 'Error', description: 'Failed to load files', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleFileConfirmed = (file) => setSelectedFile(file);

  const handleUpload = async () => {
    if (!selectedFile)
      return toast({ title: 'No file selected', description: 'Please select a file first', variant: 'destructive' });
    try {
      setUploading(true);
      const response = await companyFilesAPI.uploadFile(selectedFile, {
        category: uploadCategory,
        description: fileDescription,
      });
      if (response.success) {
        await loadFiles();
        setSelectedFile(null);
        setFileDescription('');
        toast({ title: 'File Saved', description: 'The file has been uploaded.' });
      }
    } catch (error) {
      toast({ title: 'Upload Failed', description: error.message || 'Could not upload file.', variant: 'destructive' });
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Delete file?')) return;
    try {
      const response = await companyFilesAPI.deleteFile(id);
      if (response.success) {
        await loadFiles();
        toast({ title: 'File Deleted' });
      }
    } catch {
      toast({ title: 'Error', description: 'Failed to delete file', variant: 'destructive' });
    }
  };

  // ── Viewer: fetch as blob to avoid X-Frame / CORS issues ──────────────────
  const handleViewFile = async (file) => {
    setViewerName(file.filename || 'file');
    setViewerType(file.file_type || '');
    setViewerUrl('');
    setViewerError('');
    setViewerLoading(true);
    setViewerOpen(true);

    try {
      const url = `${API_BASE}/download.php?id=${file.id}`;
      const res = await fetch(url, { credentials: 'include' });

      if (!res.ok) throw new Error(`Server responded with ${res.status}`);

      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      setViewerUrl(blobUrl);
    } catch (err) {
      console.error(err);
      setViewerError('Could not load file preview. Try downloading instead.');
    } finally {
      setViewerLoading(false);
    }
  };

  const handleCloseViewer = () => {
    setViewerOpen(false);
    setViewerLoading(false);
    setViewerError('');
    // blob URL is revoked in the useEffect above
  };

  const handleDownload = (file) => {
    const fileUrl = `${API_BASE}/download.php?id=${file.id}&dl=1`;
    const link = document.createElement('a');
    link.href = fileUrl;
    link.download = file.filename || 'file';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ── Filter ────────────────────────────────────────────────────────────────
  const filtered = files.filter((f) => {
    if (!f) return false;
    const s = searchTerm.toLowerCase();
    const searchMatch =
      !searchTerm ||
      (f.filename && f.filename.toLowerCase().includes(s)) ||
      (f.description && f.description.toLowerCase().includes(s));
    const categoryMatch = categoryFilter === 'All' || f.category === categoryFilter;
    return searchMatch && categoryMatch;
  });

  // ── Render ────────────────────────────────────────────────────────────────
  if (loading)
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-safety-orange" />
      </div>
    );

  return (
    <>
      <Helmet><title>Company Files</title></Helmet>

      <div className="space-y-6">
        <div>
          <h1 className="text-3xl font-bold text-navy-steal">Company Files</h1>
          <p className="text-stone-grey">Manage and organize valid company documents</p>
        </div>

        <div className="grid lg:grid-cols-3 gap-6">
          {/* ── File list ── */}
          <div className="lg:col-span-2 space-y-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
                <Input
                  placeholder="Search files..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10"
                />
              </div>
              <div className="w-full sm:w-[200px]">
                <Select value={categoryFilter} onValueChange={setCategoryFilter}>
                  <SelectTrigger><SelectValue placeholder="Filter by" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="All">All Categories</SelectItem>
                    <SelectItem value="General">General</SelectItem>
                    <SelectItem value="Legal">Legal</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <Card className="shadow-sm">
              <CardContent className="p-0">
                <Table>
                  <TableHeader className="bg-slate-50">
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Description</TableHead>
                      <TableHead>Category</TableHead>
                      <TableHead>Size</TableHead>
                      <TableHead>Date</TableHead>
                      <TableHead className="text-right">Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {filtered.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-stone-400 py-10">
                          No files found.
                        </TableCell>
                      </TableRow>
                    )}
                    {filtered.map((file) => (
                      <TableRow key={file.id}>
                        <TableCell className="font-medium text-navy-steal">
                          <div className="flex items-center gap-2">
                            <File className="w-4 h-4 text-safety-orange shrink-0" />
                            <span
                              className="truncate max-w-[180px] cursor-pointer hover:text-safety-orange hover:underline"
                              title={file.filename}
                              onClick={() => handleViewFile(file)}
                            >
                              {file.filename}
                            </span>
                          </div>
                        </TableCell>
                        <TableCell className="text-xs text-stone-500">
                          {file.description || 'No description'}
                        </TableCell>
                        <TableCell>{file.category}</TableCell>
                        <TableCell>
                          {file.filesize
                            ? `${(file.filesize / 1024 / 1024).toFixed(2)} MB`
                            : '-'}
                        </TableCell>
                        <TableCell>
                          {file.uploaded_at
                            ? new Date(file.uploaded_at).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button size="icon" variant="ghost" title="Preview" onClick={() => handleViewFile(file)}>
                            <Eye className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" title="Download" onClick={() => handleDownload(file)}>
                            <Download className="w-4 h-4" />
                          </Button>
                          <Button size="icon" variant="ghost" title="Delete" onClick={() => handleDelete(file.id)}>
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </div>

          {/* ── Upload card ── */}
          <div>
            <Card className="border-t-4 border-t-safety-orange sticky top-24">
              <CardHeader>
                <CardTitle className="text-lg">Upload New File</CardTitle>
                <CardDescription>Select category and upload document</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                <Select value={uploadCategory} onValueChange={setUploadCategory}>
                  <SelectTrigger><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="General">General</SelectItem>
                    <SelectItem value="Legal">Legal</SelectItem>
                    <SelectItem value="HR">HR</SelectItem>
                    <SelectItem value="Finance">Finance</SelectItem>
                  </SelectContent>
                </Select>

                <Textarea
                  placeholder="Enter description (optional)"
                  value={fileDescription}
                  onChange={(e) => setFileDescription(e.target.value)}
                />

                <FileUpload
                  onFileSelect={handleFileConfirmed}
                  maxSizeMB={250}
                  disabled={uploading}
                />

                {selectedFile && (
                  <div className="text-xs text-green-600">
                    Selected File: {selectedFile.name}
                  </div>
                )}

                <Button
                  className="w-full"
                  onClick={handleUpload}
                  disabled={!selectedFile || uploading}
                >
                  {uploading ? (
                    <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>
                  ) : 'Upload File'}
                </Button>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>

      {/* ── File Viewer Modal ── */}
      {viewerOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/60"
          onClick={(e) => { if (e.target === e.currentTarget) handleCloseViewer(); }}
        >
          <div className="bg-white w-[92%] h-[92%] rounded-xl shadow-2xl flex flex-col overflow-hidden">

            {/* Header */}
            <div className="flex items-center justify-between px-4 py-3 border-b bg-slate-50 shrink-0">
              <div className="flex items-center gap-2">
                <File className="w-4 h-4 text-safety-orange" />
                <h3 className="text-sm font-semibold text-navy-steal truncate max-w-[60vw]">
                  {viewerName}
                </h3>
              </div>
              <Button size="sm" variant="ghost" onClick={handleCloseViewer}>
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Body */}
            <div className="flex-1 w-full flex items-center justify-center overflow-auto bg-gray-100">

              {/* Loading */}
              {viewerLoading && (
                <div className="flex flex-col items-center gap-3 text-stone-500">
                  <Loader2 className="w-10 h-10 animate-spin text-safety-orange" />
                  <p className="text-sm">Loading preview…</p>
                </div>
              )}

              {/* Error */}
              {!viewerLoading && viewerError && (
                <div className="flex flex-col items-center gap-4 text-center px-6">
                  <p className="text-red-500 text-sm">{viewerError}</p>
                </div>
              )}

              {/* Image preview */}
              {!viewerLoading && !viewerError && viewerUrl && viewerType.startsWith('image/') && (
                <img
                  src={viewerUrl}
                  alt={viewerName}
                  className="max-h-full max-w-full object-contain p-4"
                />
              )}

              {/* PDF preview */}
              {!viewerLoading && !viewerError && viewerUrl && viewerType === 'application/pdf' && (
                <object
                  data={viewerUrl}
                  type="application/pdf"
                  className="w-full h-full"
                  aria-label={viewerName}
                >
                  <div className="flex flex-col items-center gap-3 p-8 text-center text-stone-500">
                    <p className="text-sm">
                      Your browser cannot display this PDF inline.
                    </p>
                    <a
                      href={viewerUrl}
                      target="_blank"
                      rel="noreferrer"
                      className="text-safety-orange underline text-sm font-medium"
                    >
                      Open in new tab
                    </a>
                  </div>
                </object>
              )}

              {/* Unsupported type */}
              {!viewerLoading && !viewerError && viewerUrl &&
                !viewerType.startsWith('image/') && viewerType !== 'application/pdf' && (
                <div className="flex flex-col items-center gap-3 text-center text-stone-500 p-8">
                  <File className="w-12 h-12 text-stone-300" />
                  <p className="text-sm">Preview not available for this file type.</p>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default CompanyFilesPage;